import moment from 'moment';

/**
 * Get user information from localStorage
 * @returns {Object} User information and employment details
 */
export const getUserInfo = () => {
  const userStr = localStorage.getItem('user');
  const user = JSON.parse(userStr || '{}');
  // Check multiple paths: login stores under hairdresserInfo or hairdresser,
  // profile-switch may nest as hairdresser.hairdresser (legacy double-nesting)
  const hdDirect = user?.hairdresserInfo || user?.hairdresser || {};
  const hdNested = hdDirect?.hairdresser || {};
  // Use whichever level has employmentTypeId defined
  const hairdresserInfo = hdDirect?.employmentTypeId != null ? hdDirect
    : hdNested?.employmentTypeId != null ? hdNested
    : hdDirect;
  const salonId = localStorage.getItem('salonId');

  // Convert employment type and salon owner status to numbers for comparison
  const employmentTypeId = parseInt(hairdresserInfo?.employmentTypeId || 0);
  const isSalonOwner = parseInt(hairdresserInfo?.isSalonOwner || 0);
  
  return {
    user,
    hairdresserInfo,
    salonId,
    employmentTypeId,
    isSalonOwner
  };
};

/**
 * Check if the user is a self-employed staff member (not salon owner)
 * @returns {Boolean} True if user is self-employed staff
 */
export const isUserSelfEmployed = () => {
  const { hairdresserInfo, employmentTypeId, isSalonOwner } = getUserInfo();
  return hairdresserInfo && employmentTypeId === 2 && isSalonOwner !== 1;
};

/**
 * Format date to display format
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted date string
 */
export const formatDate = (date) => {
  return moment(date).format('dddd, Do MMMM YYYY');
};

/**
 * Format time for last edit display
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted time string
 */
export const formatLastEditTime = (date) => {
  if (!date) return '';
  return moment(date).format('HH:mm [on] DD.MM.YY');
};

/**
 * Convert date from display format to API format
 * @param {String} date - Date in DD/MM/YYYY format
 * @returns {String} Date in YYYY-MM-DD format
 */
export const formatDateForApi = (date) => {
  return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
};

/**
 * Get default date range (current month)
 * @returns {Object} Object with startDate and endDate
 */
export const getDefaultDateRange = () => {
  return {
    startDate: moment().startOf('month').format('DD/MM/YYYY'),
    endDate: moment().endOf('month').format('DD/MM/YYYY')
  };
};

/**
 * Create cashup parameters based on user type
 * @param {Object} pagination - Pagination parameters
 * @param {Object} dateRange - Date range parameters
 * @returns {Object} Parameters for cashup API
 */
export const createCashupParams = (pagination, dateRange) => {
  const { salonId, user } = getUserInfo();
  
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    startDate: formatDateForApi(dateRange.startDate),
    endDate: formatDateForApi(dateRange.endDate),
    salonId: salonId
  };
  
  // Set cashupType and hairdresserId based on employment type
  if (isUserSelfEmployed()) {
    // For self-employed staff, only show their own cash ups
    params.cashupType = 2; // Self-employed staff cash up
    params.hairdresserId = user._id; // Filter by hairdresser ID
  } else {
    // For salon users, only show salon cash ups
    params.cashupType = 1; // Salon cash up
  }
  
  return params;
};

/**
 * Create new cashup object based on user type
 * @returns {Object} New cashup object for API
 */
export const createNewCashupObject = () => {
  const { salonId, user } = getUserInfo();
  
  // Create cashup with all required fields according to API model
  const newCashup = {
    date: moment().format('YYYY-MM-DD'), // Date in ISO format
    salonId: salonId, // Salon ID is required
    cashHolder: 1 // Default to salon holds the cash (1)
  };
  
  // Set cashupType based on employment type
  if (isUserSelfEmployed()) {
    newCashup.cashupType = 2; // Self-employed staff cash up
    newCashup.hairdresserId = user._id; // Include hairdresser ID for self-employed staff
    newCashup.cashHolder = 2; // Staff holds the cash for self-employed
  } else {
    newCashup.cashupType = 1; // Salon cash up
  }
  
  return newCashup;
};